import { createStore, applyMiddleware, compose } from 'redux';
import { loadUser } from 'redux-oidc';
import thunk from 'redux-thunk';
import { createMiddleware } from 'redux-beacon';
import logger from '@redux-beacon/logger';
import GoogleAnalytics, {
  trackPageView,
  trackEvent,
} from '@redux-beacon/google-analytics';
import rootReducer from '../reducer';
import userManager from '../utils/usermanager';

import {
  routeChanged,
  userSearch,
  briefPrinted,
  logAnalytics,
  logClicks,
  logFilters,
  logEvent,
  logDownloads,
} from '../actions/usagelogging';
import { logPageView } from '../api';

import { EXPORT_BRIEF_REQUESTED } from '../actions/exportActionTypes';
import { SEARCH_FILTER } from '../utils/google-analytics-labels';

const loggerMiddleware = (store) => (next) => (action) => {
  next(action);
};

const ga = GoogleAnalytics();

const eventsMap = {
  [routeChanged]: trackPageView((action) => ({
    page: action.location.pathname,
  })),
  [briefPrinted]: trackEvent((action) => ({
    category: 'Brief',
    action: 'print',
    label: action.url,
  })),
  [userSearch]: trackEvent((action) => ({
    category: 'Search',
    action: 'search',
    label: action.searchPhrase,
  })),
  [logAnalytics]: trackEvent((action) => ({
    category: action.log.category,
    action: action.log.action,
    label: action.log.label,
  })),

  [logClicks]: trackEvent((action) => {
    return {
      category: action.category,
      action: action.origin,
      label: action.organization,
      value: action.uniqueId,
    };
  }),
  [logFilters]: trackEvent((action) => {
    return {
      category: SEARCH_FILTER,
      action: action.filterOrigin,
      label: action.filterValue,
      value: 0,
    };
  }),
  [logEvent]: trackEvent((action) => {
    return {
      category: action.category,
      action: action.action,
      label: action.label,
      value: action.value,
    };
  }),

  [EXPORT_BRIEF_REQUESTED]: trackEvent((action) => ({
    category: 'Export',
    action: 'export',
    label: action.payload.exportType,
  })),
  [logDownloads]: trackEvent((action) => {
    return {
      category: 'Download',
      action: action.category,
      label: action.user.profile.email,
      value: 0,
    };
  }),
  'redux-oidc/USER_FOUND': (action) => {
    if (window.ga && action.payload && action.payload.profile) {
      const crmId =
        action.payload.profile[
          'http://schemas.ecri.org/sharepoint/2013/claims/crmguid'
        ];
      if (crmId) {
        window.ga('set', 'userId', crmId);
      }
    }
  },
};
let gaMiddleware;
if (process.env.NODE_ENV !== 'production') {
  gaMiddleware = createMiddleware(eventsMap, ga, { logger });
} else {
  gaMiddleware = createMiddleware(eventsMap, ga);
}

const usageLogMiddleware = (store) => (next) => (action) => {
  // we only put stuff into the usage log for logged in users

  const state = store.getState();
  const user =
    state.oidc && state.oidc.user && state.oidc.user.access_token
      ? state.oidc.user
      : null;

  if (user) {
    switch (action.type) {
      case routeChanged: {
        logPageView(
          {
            userId: user.profile.email,
            activityId: 4,
            siteId: Number(process.env.REACT_APP_SITE_ID),
            sourceId: 1,
            crmId:
              action.user.profile[
                'http://schemas.ecri.org/sharepoint/2013/claims/crmguid'
              ],
            detail: action.location.pathname,
          },
          user
        );
        break;
      }
      case userSearch: {
        logPageView(
          {
            userId: user.profile.email,
            activityId: 8,
            siteId: Number(process.env.REACT_APP_SITE_ID),
            sourceId: 1,
            crmId:
              action.user.profile[
                'http://schemas.ecri.org/sharepoint/2013/claims/crmguid'
              ],
            detail: `Guidelines Search - ${action.searchPhrase}`,
          },
          user
        );
        break;
      }
      case briefPrinted: {
        logPageView(
          {
            userId: user.profile.email,
            activityId: 4,
            siteId: Number(process.env.REACT_APP_SITE_ID),
            sourceId: 1,
            crmId:
              user.profile[
                'http://schemas.ecri.org/sharepoint/2013/claims/crmguid'
              ],
            detail: `Guidelines Brief - Print - ${action.url}`,
          },
          user
        );
        break;
      }
      case EXPORT_BRIEF_REQUESTED: {
        logPageView(
          {
            userId: user.profile.email,
            activityId: 4,
            siteId: Number(process.env.REACT_APP_SITE_ID),
            sourceId: 1,
            crmId:
              user.profile[
                'http://schemas.ecri.org/sharepoint/2013/claims/crmguid'
              ],
            detail: `Guidelines - Export - ${action.payload.exportType}`,
          },
          user
        );
        break;
      }
      case logClicks: {
        logPageView(
          {
            userId: user.profile.email,
            activityId: 4,
            siteId: Number(process.env.REACT_APP_SITE_ID),
            sourceId: 1,
            crmId:
              user.profile[
                'http://schemas.ecri.org/sharepoint/2013/claims/crmguid'
              ],
            detail: `Guidelines-Links|${action.origin}|${action.organization}|${action.url}|${action.uniqueId}`,
          },
          user
        );
        break;
      }
      case logFilters: {
        logPageView(
          {
            userId: user.profile.email,
            activityId: 4,
            siteId: Number(process.env.REACT_APP_SITE_ID),
            sourceId: 1,
            crmId:
              user.profile[
                'http://schemas.ecri.org/sharepoint/2013/claims/crmguid'
              ],
            detail: `Guidelines-Filter|${action.filterOrigin}|${action.filterValue}`,
          },
          user
        );
        break;
      }
      case logEvent: {
        logPageView(
          {
            userId: user.profile.email,
            activityId: 4,
            siteId: Number(process.env.REACT_APP_SITE_ID),
            sourceId: 1,
            crmId:
              user.profile[
                'http://schemas.ecri.org/sharepoint/2013/claims/crmguid'
              ],
            detail: `Guidelines-Event|${action.category}|${action.action}|${action.label}|${action.value}`,
          },
          user
        );
        break;
      }
      case logDownloads: {
        logPageView(
          {
            userId: user.profile.email,
            activityId: 4,
            siteId: Number(process.env.REACT_APP_SITE_ID),
            sourceId: 1,
            crmId:
              user.profile[
                'http://schemas.ecri.org/sharepoint/2013/claims/crmguid'
              ],
            detail: `Guidelines-Download|${action.category}`,
          },
          user
        );
        break;
      }
      default: {
      }
    }
  }
  next(action);
};

const initialState = {};

const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

const createStoreWithMiddleware = composeEnhancers(
  applyMiddleware(loggerMiddleware, usageLogMiddleware, gaMiddleware, thunk)
)(createStore);

const store = createStoreWithMiddleware(rootReducer, initialState);

loadUser(store, userManager);

export default store;
